/**
 * PropPicker.tsx
 * Snap-scroll container for prop/objective selection
 * 
 * Single Responsibility: Render scrollable prop card container
 * Props In: player, props, rivals, onSelectProp
 * Events Out: none (delegates to PropCard)
 * 
 * Reference: TunnelMockupsV5_Final.jsx lines 452-507
 * 
 * BUCKET 4 FIX: Added null-safe check for props array
 */
import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { PropCard, Prop } from './PropCard';

interface Player {
  id: number;
  name: string;
  team: string;
  teamColor: string;
  opponent: string;
}

interface PropPickerProps {
  player: Player;
  props: Prop[];
  rivalsByProp: Record<number, string[]>;
  onSelectProp: (prop: Prop) => void;
  onChangePlayer: () => void;
}

export const PropPicker: React.FC<PropPickerProps> = ({
  player,
  props,
  rivalsByProp,
  onSelectProp,
  onChangePlayer,
}) => {
  // BUCKET 4 FIX: Null-safe check for props
  if (!props || props.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-slate-500 text-sm">No props available for {player.name}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Selected Player Summary */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-black text-white"
            style={{ backgroundColor: player.teamColor }}
          >
            {player.team}
          </div>
          <div className="flex-1">
            <p className="font-bold text-white">{player.name}</p>
            <p className="text-[11px] text-slate-400">vs {player.opponent}</p>
          </div>
          <button 
            onClick={onChangePlayer}
            className="text-[11px] text-indigo-400 font-bold uppercase tracking-wide min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            Change
          </button>
        </div>
      </div>

      {/* Prop Cards Carousel */}
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-6 pb-4 scrollbar-hide">
        {props.map((prop) => {
          const rivals = rivalsByProp[prop.id] || [];
          
          return (
            <PropCard
              key={prop.id}
              prop={prop}
              rivals={rivals}
              onSelect={onSelectProp}
            />
          );
        })}
      </div>

      {/* Pagination indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {props.map((_, idx) => (
          <div 
            key={idx}
            className={`h-1.5 rounded-full transition-all ${
              idx === 0 ? 'w-6 bg-white' : 'w-1.5 bg-slate-700'
            }`}
          />
        ))}
      </div>
      <p className="text-center text-slate-500 text-[11px] mt-2 uppercase tracking-wide">
        Swipe to see all targets
      </p>

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default PropPicker;
